import { DependencyList, useEffect, useRef, useState } from 'react';

import { Photo } from 'Compass/profile-lab/types';
import { db } from 'Compass/profile-lab/services/db';
import { attachPhotoStatus } from 'Compass/profile-lab/utils/assetFormatting';

interface UsePhotoWithStatusResult {
  photos: Photo[];
  loading: boolean;
  error: string | null;
}

export const usePhotoWithStatus = (
  fetchPhotos: () => Promise<Photo[]>,
  dependencies: DependencyList = []
): UsePhotoWithStatusResult => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchPhotosRef = useRef(fetchPhotos);
  fetchPhotosRef.current = fetchPhotos;

  useEffect(() => {
    const loadPhotos = async () => {
      setLoading(true);
      setError(null);

      try {
        await db.init();
        const fetchedPhotos = await fetchPhotosRef.current();
        const selectionState = await db.getSelectionState();

        const selectedSet = new Set(selectionState.selected);
        const growSet = new Set(selectionState.grow);

        const photosWithStatus = attachPhotoStatus(fetchedPhotos, selectedSet, growSet);
        setPhotos(photosWithStatus);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load photos';
        setError(errorMessage);
        console.error('Failed to fetch photos:', err);
      } finally {
        setLoading(false);
      }
    };

    void loadPhotos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return { photos, loading, error };
};
